import { lazy, Suspense, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import {
  Activity,
  AirVent,
  AlertTriangle,
  ArrowRight,
  BarChart3,
  Bot,
  Building2,
  CheckCircle2,
  ChevronDown,
  Clipboard,
  Clock3,
  Copy,
  Database,
  Gauge,
  Home,
  KeyRound,
  Layers,
  Lightbulb,
  LogOut,
  Menu,
  Pause,
  Play,
  Radio,
  RefreshCw,
  Search,
  Send,
  Server,
  ShieldAlert,
  Sparkles,
  ThermometerSun,
  Waves,
  X
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import { BrowserRouter, Navigate, NavLink, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { DEVICE_COLORS, METRIC_TRENDS, PROMPT_BUTTONS, SOURCE_CARDS, WINDOW_OPTIONS } from "./consoleConfig";
import { telemetryGraphPath, type TelemetryGraphDetail, type TelemetryGraphNode, type TelemetryGraphPayload } from "./telemetryGraph";
import "./styles.css";

const TelemetryGraph3D = lazy(() => import("./TelemetryGraph3D"));
const TelemetryGraph2D = lazy(() => import("./TelemetryGraph2D"));
const TelemetryDataTree = lazy(() => import("./TelemetryDataTree"));

type HealthPayload = {
  status: string;
  app: string;
  environment: string;
  openai_configured: boolean;
  llm_provider: string;
  llm_model: string;
  elasticsearch_url_configured: boolean;
  mcp_url_configured: boolean;
  openai_mcp_url_configured: boolean;
  web_mcp_enabled: boolean;
  web_mcp_url_configured: boolean;
  openai_web_mcp_url_configured: boolean;
  site_auth_enabled: boolean;
  telemetry_memory_enabled: boolean;
  telemetry_memory_index_configured: boolean;
  outside_context_enabled: boolean;
  outside_context_index_configured: boolean;
  outside_context_location_configured: boolean;
};

type AuthSessionPayload = {
  auth_required: boolean;
  authenticated: boolean;
  ttl_days: number;
};

type AsyncState<T> =
  | { state: "loading" }
  | { state: "ready"; payload: T }
  | { state: "unavailable"; message: string };

type SourceScores = {
  workspace_score: number | null;
  air_quality_score: number | null;
  comfort_score: number | null;
};

type LatestMetrics = {
  occupied?: boolean | null;
  temp_c?: number | null;
  humidity_pct?: number | null;
  tvoc_ugm3?: number | null;
  co2_ppm?: number | null;
  pm10_ugm3?: number | null;
  pm25_ugm3?: number | null;
  light_lux?: number | null;
  color_temp_k?: number | null;
  noise_dba?: number | null;
};

type SeriesPoint = LatestMetrics & {
  timestamp: string | null;
  inside_wellness_score: number | null;
  score_state: string;
};

type OutsideMetrics = {
  temp_c?: number | null;
  apparent_temp_c?: number | null;
  humidity_pct?: number | null;
  precipitation_mm?: number | null;
  wind_speed_kmh?: number | null;
  weather_code?: number | null;
  pm10_ugm3?: number | null;
  pm25_ugm3?: number | null;
  us_aqi?: number | null;
  us_aqi_pm25?: number | null;
  us_aqi_pm10?: number | null;
  carbon_monoxide_ugm3?: number | null;
  nitrogen_dioxide_ugm3?: number | null;
  ozone_ugm3?: number | null;
};

type OutsideSeriesPoint = OutsideMetrics & {
  timestamp: string | null;
};

type OutsideComparisonMetric = {
  inside_average?: number | null;
  outside?: number | null;
  inside_minus_outside_c?: number | null;
  inside_minus_outside_pct?: number | null;
  inside_minus_outside_ugm3?: number | null;
};

type OutsideContext = {
  state: string;
  message: string;
  provider?: string;
  source_kind?: string;
  index?: string;
  latest_timestamp?: string | null;
  latest_metrics?: OutsideMetrics;
  series?: OutsideSeriesPoint[];
  summary?: string;
  location?: {
    id?: string;
    label?: string;
    latitude?: number | null;
    longitude?: number | null;
    timezone?: string;
    approximate?: boolean;
  };
  comparisons?: {
    temperature?: OutsideComparisonMetric;
    humidity?: OutsideComparisonMetric;
    pm25?: OutsideComparisonMetric;
    pm10?: OutsideComparisonMetric;
  };
};

type ComponentScore = {
  name: string;
  label: string;
  weight: number;
  score: number;
  metrics: Array<{
    name: string;
    label: string;
    value: number | string | null;
    score: number;
  }>;
};

type Anomaly = {
  code: string;
  severity: "info" | "warning" | "critical" | string;
  message: string;
  metric: string | null;
  value: number | null;
  threshold: number | { low: number; high: number } | null;
};

type DeviceInsight = {
  device_id: string;
  room_label: string;
  latest_timestamp: string | null;
  reading_count: number;
  freshness: "fresh" | "lagging" | "stale" | "offline" | string;
  freshness_lag_hours: number | null;
  inside_wellness_score: number | null;
  score_state: string;
  source_scores: SourceScores;
  components: ComponentScore[];
  latest_metrics: LatestMetrics;
  series: SeriesPoint[];
  explanations: string[];
  anomalies: Anomaly[];
};

type InsightsPayload = {
  schema_version: string;
  room_label: string;
  window: {
    hours: number;
    room_anchor_timestamp: string | null;
  };
  outside_context: OutsideContext;
  devices: DeviceInsight[];
  comparison: {
    leader_device_id: string | null;
    summary: string;
    caveats: string[];
  };
};

type ChatRole = "user" | "assistant";

type ChatMessage = {
  role: ChatRole;
  content: string;
};

type ChatToolSummary = {
  kind: string;
  label: string;
  status: string;
  detail?: string;
  name?: string;
  tool_names?: string[];
};

type ChatSource = {
  kind: string;
  label: string;
  status: string;
  detail?: string;
  url?: string;
};

type ChatMemoryDocument = {
  document_id?: string;
  document_type?: string;
  title?: string;
  room_label?: string;
  device_id?: string | null;
  window_hours?: number;
  anchor_timestamp?: string | null;
  latest_timestamp?: string | null;
  freshness?: string | null;
  inside_wellness_score?: number | null;
  anomaly_count?: number;
  highest_severity?: string | null;
  score?: number;
};

type ChatMemory = {
  status?: string;
  message?: string;
  index?: string;
  top_k?: number;
  embedding_model?: string;
  documents: ChatMemoryDocument[];
};

type ChatPayload = {
  status: string;
  answer: string;
  tools: ChatToolSummary[];
  sources: ChatSource[];
  model: string;
  mcp_mode: string;
  web_mcp_enabled: boolean;
  window_hours: number;
  memory?: ChatMemory;
};

type ChatEntry = {
  id: number;
  question: string;
  answer: string;
  payload: ChatPayload;
};

type DisplayDevice = {
  device: DeviceInsight;
  point: SeriesPoint | null;
  score: number | null;
};

type MetricDefinition = (typeof METRIC_TRENDS)[number];
type OutsideMetricKey = keyof OutsideMetrics;

const OUTSIDE_TREND_FIELDS: Partial<Record<keyof SeriesPoint, OutsideMetricKey>> = {
  temp_c: "temp_c",
  humidity_pct: "humidity_pct",
  pm25_ugm3: "pm25_ugm3",
  pm10_ugm3: "pm10_ugm3"
};

type TelemetryGraphView = "3d" | "2d" | "tree";

type AnomalyView = Anomaly & {
  id: string;
  deviceId: string;
  deviceName: string;
  category: string;
  latestTimestamp: string | null;
};

type DrawerContent = {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  accent?: string;
  body: ReactNode;
};

type ConsoleState = {
  health: AsyncState<HealthPayload>;
  insights: AsyncState<InsightsPayload>;
  telemetryGraph: AsyncState<TelemetryGraphPayload>;
  payload: InsightsPayload | null;
  windowHours: number;
  setWindowHours: (hours: number) => void;
  graphDetail: TelemetryGraphDetail;
  setGraphDetail: (detail: TelemetryGraphDetail) => void;
  serviceState: ReturnType<typeof resolveServiceState>;
  openAiReady: boolean;
  timeline: string[];
  replayIndex: number;
  setReplayIndex: (index: number) => void;
  isPlaying: boolean;
  setIsPlaying: (value: boolean) => void;
  currentReplayTimestamp: string | null;
  displayDevices: DisplayDevice[];
  roomScore: number | null;
  statusCounts: ReturnType<typeof countFreshness>;
  anomalyItems: AnomalyView[];
  chatInput: string;
  setChatInput: (value: string) => void;
  chatResult: AsyncState<ChatPayload> | { state: "idle" };
  chatMessages: ChatMessage[];
  chatEntries: ChatEntry[];
  copiedEntryId: number | null;
  setCopiedEntryId: (value: number | null) => void;
  submitChat: (overridePrompt?: string) => Promise<void>;
  dismissChatEntry: (id: number) => void;
  logoutSite: () => Promise<void>;
  openDrawer: (content: DrawerContent) => void;
};

const NAV_GROUPS = [
  {
    label: "Overview",
    items: [
      { to: "/", icon: Home, text: "Command Center", end: true },
      { to: "/wellcubes", icon: Gauge, text: "Wellcubes" },
      { to: "/trends", icon: BarChart3, text: "Trends" },
      { to: "/visualize", icon: Layers, text: "Visualize" }
    ]
  },
  {
    label: "Operations",
    items: [
      { to: "/operations", icon: ShieldAlert, text: "Anomalies" },
      { to: "/replay", icon: Radio, text: "Replay" }
    ]
  },
  {
    label: "Intelligence",
    items: [
      { to: "/ai-query", icon: Sparkles, text: "AI Query" },
      { to: "/sources", icon: Database, text: "Sources" }
    ]
  }
];

export default function App() {
  return (
    <BrowserRouter>
      <WellcubeConsole />
    </BrowserRouter>
  );
}

function WellcubeConsole() {
  const [authSession, setAuthSession] = useState<AsyncState<AuthSessionPayload>>({ state: "loading" });
  const [authError, setAuthError] = useState("");
  const [authBusy, setAuthBusy] = useState(false);
  const [health, setHealth] = useState<AsyncState<HealthPayload>>({ state: "loading" });
  const [insights, setInsights] = useState<AsyncState<InsightsPayload>>({ state: "loading" });
  const [telemetryGraph, setTelemetryGraph] = useState<AsyncState<TelemetryGraphPayload>>({ state: "loading" });
  const [windowHours, setWindowHours] = useState(24);
  const [graphDetail, setGraphDetail] = useState<TelemetryGraphDetail>("summary");
  const [replayIndex, setReplayIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatEntries, setChatEntries] = useState<ChatEntry[]>([]);
  const [chatResult, setChatResult] = useState<AsyncState<ChatPayload> | { state: "idle" }>({ state: "idle" });
  const [copiedEntryId, setCopiedEntryId] = useState<number | null>(null);
  const [drawer, setDrawer] = useState<DrawerContent | null>(null);
  const [drawerMounted, setDrawerMounted] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const nextChatId = useRef(1);
  const location = useLocation();

  useEffect(() => {
    let active = true;

    fetchJson<AuthSessionPayload>("/api/auth/session")
      .then((payload) => {
        if (active) {
          setAuthSession({ state: "ready", payload });
        }
      })
      .catch((error: Error) => {
        if (active) {
          setAuthSession({ state: "unavailable", message: error.message });
        }
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    fetchJson<HealthPayload>("/health")
      .then((payload) => {
        if (active) {
          setHealth({ state: "ready", payload });
        }
      })
      .catch((error: Error) => {
        if (active) {
          setHealth({ state: "unavailable", message: error.message });
        }
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (authSession.state === "loading") {
      return undefined;
    }

    if (authSession.state === "unavailable") {
      setInsights({ state: "unavailable", message: authSession.message });
      return undefined;
    }

    if (!authSession.payload.authenticated) {
      setInsights({ state: "unavailable", message: "Site access is required before loading telemetry." });
      return undefined;
    }

    let active = true;
    setInsights({ state: "loading" });

    fetchJson<InsightsPayload>(`/api/wellcube/insights?window_hours=${windowHours}`)
      .then((payload) => {
        if (active) {
          setInsights({ state: "ready", payload });
        }
      })
      .catch((error: Error) => {
        if (active) {
          setInsights({ state: "unavailable", message: error.message });
        }
      });

    return () => {
      active = false;
    };
  }, [authSession, windowHours]);

  useEffect(() => {
    if (location.pathname !== "/visualize") {
      return undefined;
    }

    if (authSession.state === "loading") {
      return undefined;
    }

    if (authSession.state === "unavailable") {
      setTelemetryGraph({ state: "unavailable", message: authSession.message });
      return undefined;
    }

    if (!authSession.payload.authenticated) {
      setTelemetryGraph({ state: "unavailable", message: "Site access is required before loading telemetry graph data." });
      return undefined;
    }

    let active = true;
    setTelemetryGraph({ state: "loading" });

    fetchJson<TelemetryGraphPayload>(telemetryGraphPath(windowHours, graphDetail))
      .then((payload) => {
        if (active) {
          setTelemetryGraph({ state: "ready", payload });
        }
      })
      .catch((error: Error) => {
        if (active) {
          setTelemetryGraph({ state: "unavailable", message: error.message });
        }
      });

    return () => {
      active = false;
    };
  }, [authSession, graphDetail, location.pathname, windowHours]);

  const payload = insights.state === "ready" ? insights.payload : null;
  const timeline = useMemo(() => buildTimeline(payload), [payload]);
  const currentReplayTimestamp = timeline[replayIndex] ?? null;
  const displayDevices = useMemo(
    () => buildDisplayDevices(payload, currentReplayTimestamp),
    [payload, currentReplayTimestamp]
  );
  const roomScore = average(displayDevices.map((item) => item.score));
  const statusCounts = countFreshness(displayDevices.map((item) => item.device));
  const anomalyItems = useMemo(() => buildAnomalyItems(payload), [payload]);
  const serviceState = useMemo(() => resolveServiceState(health), [health]);
  const openAiReady = health.state === "ready" && health.payload.openai_configured;

  useEffect(() => {
    setReplayIndex(Math.max(0, timeline.length - 1));
    setIsPlaying(false);
  }, [timeline.length, payload?.window.room_anchor_timestamp]);

  useEffect(() => {
    if (!isPlaying || timeline.length < 2) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setReplayIndex((current) => (current >= timeline.length - 1 ? 0 : current + 1));
    }, 900);

    return () => window.clearInterval(timer);
  }, [isPlaying, timeline.length]);

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = navOpen || drawer ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [navOpen, drawer]);

  useEffect(() => {
    if (!drawer) {
      return undefined;
    }

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeDrawer();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawer]);

  const openDrawer = (content: DrawerContent) => {
    setDrawer(content);
    window.requestAnimationFrame(() => setDrawerMounted(true));
  };

  const closeDrawer = () => {
    setDrawerMounted(false);
    window.setTimeout(() => setDrawer(null), 220);
  };

  const submitChat = async (overridePrompt?: string) => {
    const prompt = (overridePrompt ?? chatInput).trim();
    if (!prompt || chatResult.state === "loading" || authSession.state !== "ready" || !authSession.payload.authenticated) {
      return;
    }

    const userMessage: ChatMessage = { role: "user", content: prompt };
    const history = chatMessages.slice(-6);
    setChatMessages((messages) => [...messages, userMessage]);
    setChatInput("");
    setChatResult({ state: "loading" });

    try {
      const response = await postJson<ChatPayload>("/api/wellcube/chat", {
        message: prompt,
        window_hours: windowHours,
        history
      });
      const assistantMessage: ChatMessage = { role: "assistant", content: response.answer };
      setChatMessages((messages) => [...messages, assistantMessage]);
      setChatEntries((entries) => [
        {
          id: nextChatId.current++,
          question: prompt,
          answer: response.answer,
          payload: response
        },
        ...entries
      ]);
      setChatResult({ state: "ready", payload: response });
    } catch (error) {
      setChatResult({ state: "unavailable", message: (error as Error).message });
    }
  };

  const dismissChatEntry = (id: number) => {
    setChatEntries((entries) => entries.filter((entry) => entry.id !== id));
  };

  const unlockSite = async (passcode: string) => {
    if (!passcode.trim() || authBusy) {
      return;
    }

    setAuthBusy(true);
    setAuthError("");
    try {
      const payload = await postJson<AuthSessionPayload>("/api/auth/session", { passcode });
      setAuthSession({ state: "ready", payload });
    } catch (error) {
      setAuthError((error as Error).message);
    } finally {
      setAuthBusy(false);
    }
  };

  const logoutSite = async () => {
    try {
      const payload = await fetchJson<AuthSessionPayload>("/api/auth/session", { method: "DELETE" });
      setAuthSession({ state: "ready", payload });
    } catch {
      setAuthSession({
        state: "ready",
        payload: { auth_required: true, authenticated: false, ttl_days: 30 }
      });
    }
    setChatInput("");
    setChatMessages([]);
    setChatEntries([]);
    setChatResult({ state: "idle" });
    setInsights({ state: "unavailable", message: "Site access is required before loading telemetry." });
    setTelemetryGraph({ state: "unavailable", message: "Site access is required before loading telemetry graph data." });
    setDrawer(null);
    setDrawerMounted(false);
  };

  const context: ConsoleState = {
    health,
    insights,
    telemetryGraph,
    payload,
    windowHours,
    setWindowHours,
    graphDetail,
    setGraphDetail,
    serviceState,
    openAiReady,
    timeline,
    replayIndex,
    setReplayIndex,
    isPlaying,
    setIsPlaying,
    currentReplayTimestamp,
    displayDevices,
    roomScore,
    statusCounts,
    anomalyItems,
    chatInput,
    setChatInput,
    chatResult,
    chatMessages,
    chatEntries,
    copiedEntryId,
    setCopiedEntryId,
    submitChat,
    dismissChatEntry,
    logoutSite,
    openDrawer
  };

  if (authSession.state !== "ready" || !authSession.payload.authenticated) {
    return (
      <AccessGate
        authError={authError}
        authSession={authSession}
        busy={authBusy}
        health={health}
        onSubmit={unlockSite}
      />
    );
  }

  return (
    <div className="app">
      <Sidebar context={context} mobileOpen={navOpen} onClose={() => setNavOpen(false)} />
      {navOpen ? <button aria-label="Close menu overlay" className="nav-overlay" onClick={() => setNavOpen(false)} /> : null}
      <main className="main">
        <Topbar context={context} onOpenMenu={() => setNavOpen(true)} />
        <Routes>
          <Route path="/" element={<CommandCenter context={context} />} />
          <Route path="/wellcubes" element={<WellcubesPage context={context} />} />
          <Route path="/trends" element={<TrendsPage context={context} />} />
          <Route path="/visualize" element={<VisualizePage context={context} />} />
          <Route path="/operations" element={<OperationsPage context={context} />} />
          <Route path="/replay" element={<ReplayPage context={context} />} />
          <Route path="/ai-query" element={<AIQueryPage context={context} />} />
          <Route path="/sources" element={<SourcesPage context={context} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      {drawer ? <Drawer content={drawer} mounted={drawerMounted} onClose={closeDrawer} /> : null}
    </div>
  );
}

function AccessGate({
  authError,
  authSession,
  busy,
  health,
  onSubmit
}: {
  authError: string;
  authSession: AsyncState<AuthSessionPayload>;
  busy: boolean;
  health: AsyncState<HealthPayload>;
  onSubmit: (passcode: string) => Promise<void>;
}) {
  const [passcode, setPasscode] = useState("");
  const loading = authSession.state === "loading";
  const unavailable = authSession.state === "unavailable";
  const serviceState = resolveServiceState(health);

  return (
    <main className="access-shell">
      <section className="access-panel" aria-label="Site access">
        <div className="brand access-brand">
          <div className="brand-mark">
            <span>DW</span>
          </div>
          <div className="brand-text">
            <b>Delos Wellcube</b>
            <small>Wellness Ops</small>
          </div>
        </div>
        <div className="access-copy">
          <div className="eyebrow">Private Demo</div>
          <h1>Enter Access Key</h1>
          <p>Telemetry and AI chat are protected for this workspace.</p>
        </div>
        <form
          className="access-form"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit(passcode);
          }}
        >
          <label htmlFor="site-passcode">Access key</label>
          <div className="access-input">
            <KeyRound size={16} />
            <input
              autoComplete="current-password"
              autoFocus
              disabled={loading || busy}
              id="site-passcode"
              onChange={(event) => setPasscode(event.target.value)}
              placeholder={loading ? "Checking session..." : "Passcode"}
              type="password"
              value={passcode}
            />
          </div>
          {authError || unavailable ? (
            <div className="access-error" role="alert">
              {authError || (unavailable ? authSession.message : "")}
            </div>
          ) : null}
          <button className="btn primary access-submit" disabled={loading || busy || !passcode.trim()} type="submit">
            {busy || loading ? <RefreshCw className="spin" size={15} /> : <KeyRound size={15} />}
            {busy || loading ? "Checking" : "Unlock"}
          </button>
        </form>
        <div className="access-foot">
          <span className={`tag dot ${serviceState.ready ? "good" : "warn"}`}>{serviceState.label}</span>
          <span className="tag ghost">Session cookie · {authSession.state === "ready" ? `${authSession.payload.ttl_days}d` : "pending"}</span>
        </div>
      </section>
    </main>
  );
}

function Sidebar({ context, mobileOpen, onClose }: { context: ConsoleState; mobileOpen: boolean; onClose: () => void }) {
  const { payload, statusCounts, timeline, anomalyItems, serviceState } = context;

  return (
    <aside className={`sidebar ${mobileOpen ? "mobile-open" : ""}`} aria-label="Console navigation">
      <div className="brand">
        <div className="brand-mark">
          <span>DW</span>
        </div>
        <div className="brand-text">
          <b>Delos Wellcube</b>
          <small>Wellness Ops</small>
        </div>
        <button aria-label="Close menu" className="sidebar-close" onClick={onClose} type="button">
          <X size={18} />
        </button>
      </div>

      <nav className="nav">
        {NAV_GROUPS.map((group) => (
          <div className="nav-group" key={group.label}>
            <div className="nav-group-label">{group.label}</div>
            {group.items.map((item) => (
              <NavLink
                className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                end={"end" in item ? item.end : false}
                key={item.to}
                onClick={onClose}
                to={item.to}
              >
                <item.icon />
                <span>{item.text}</span>
                {item.to === "/wellcubes" ? <span className="badge-mini">{payload?.devices.length ?? "--"}</span> : null}
                {item.to === "/operations" ? <span className="badge-mini">{anomalyItems.length}</span> : null}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <div className="sidebar-foot">
        <div className="room-chip compact">
          <Building2 size={13} />
          <span>{payload?.room_label ?? "LA Office Suite"}</span>
        </div>
        <div className="row">
          <span>Service</span>
          <span className={`mono ${serviceState.ready ? "good" : "warn"}`}>{serviceState.label}</span>
        </div>
        <div className="row">
          <span>Replay points</span>
          <span className="mono">{formatNumber(timeline.length)}</span>
        </div>
        <div className="row">
          <span>Fresh devices</span>
          <span className="mono good">{statusCounts.fresh}</span>
        </div>
      </div>
    </aside>
  );
}

function Topbar({ context, onOpenMenu }: { context: ConsoleState; onOpenMenu: () => void }) {
  const { payload, health, serviceState, windowHours, setWindowHours, openAiReady, logoutSite } = context;
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  const matches = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) {
      return [];
    }

    const pageMatches = [
      { label: "Command Center", detail: "Overview", to: "/" },
      { label: "Wellcubes", detail: "Device inventory", to: "/wellcubes" },
      { label: "Trends", detail: "Metric explorer", to: "/trends" },
      { label: "Visualize", detail: "Telemetry graph", to: "/visualize" },
      { label: "Anomalies", detail: "Operations log", to: "/operations" },
      { label: "Replay", detail: "Telemetry timeline", to: "/replay" },
      { label: "AI Query", detail: "Assistant workspace", to: "/ai-query" },
      { label: "Sources", detail: "Methodology", to: "/sources" }
    ].filter((item) => `${item.label} ${item.detail}`.toLowerCase().includes(trimmed));

    const deviceMatches =
      payload?.devices
        .map((device, index) => ({
          label: deviceName(device, index),
          detail: device.device_id,
          to: "/wellcubes"
        }))
        .filter((item) => `${item.label} ${item.detail}`.toLowerCase().includes(trimmed)) ?? [];

    const metricMatches = METRIC_TRENDS.filter((metric) =>
      `${metric.label} ${metric.shortLabel} ${metric.unit}`.toLowerCase().includes(trimmed)
    ).map((metric) => ({
      label: metric.label,
      detail: "Trend metric",
      to: "/trends"
    }));

    return [...pageMatches, ...deviceMatches, ...metricMatches].slice(0, 7);
  }, [payload?.devices, query]);

  return (
    <div className="topbar" aria-label="Console controls">
      <button aria-label="Open menu" className="menu-btn" onClick={onOpenMenu} type="button">
        <Menu size={20} />
      </button>
      <div className="searchbox">
        <Search />
        <input
          aria-label="Search console"
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search devices, metrics, operations..."
          value={query}
        />
        {matches.length ? (
          <div className="search-results">
            {matches.map((match) => (
              <button
                key={`${match.to}-${match.label}-${match.detail}`}
                onClick={() => {
                  navigate(match.to);
                  setQuery("");
                }}
                type="button"
              >
                <span>{match.label}</span>
                <small>{match.detail}</small>
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <div className="topbar-actions">
        <SegmentedWindowControl setWindowHours={setWindowHours} windowHours={windowHours} />
        <span className={`tag dot ${serviceState.ready ? "good" : "warn"}`}>{serviceState.label}</span>
        <span className={`tag ${openAiReady ? "good" : "warn"}`}>
          <Bot size={13} />
          {openAiReady ? "AI ready" : "AI idle"}
        </span>
        <span className={`tag hide-sm ${health.state === "ready" && health.payload.web_mcp_enabled ? "good" : "ghost"}`}>
          <Search size={13} />
          {health.state === "ready" && health.payload.web_mcp_enabled ? "Web MCP" : "Web off"}
        </span>
        <button aria-label="Log out" className="icon-action" onClick={logoutSite} title="Log out" type="button">
          <LogOut size={14} />
        </button>
      </div>
    </div>
  );
}

function SegmentedWindowControl({
  windowHours,
  setWindowHours
}: {
  windowHours: number;
  setWindowHours: (hours: number) => void;
}) {
  return (
    <div className="seg" aria-label="Telemetry window">
      {WINDOW_OPTIONS.map((hours) => (
        <button className={hours === windowHours ? "active" : ""} key={hours} onClick={() => setWindowHours(hours)} type="button">
          {hours === 168 ? "7d" : `${hours}h`}
        </button>
      ))}
    </div>
  );
}

function PageStatus({ context }: { context: ConsoleState }) {
  const { insights } = context;
  if (insights.state === "loading") {
    return <StateBanner icon={<RefreshCw className="spin" size={16} />} title="Loading telemetry" message="Fetching scored Wellcube insights from Elasticsearch." />;
  }

  if (insights.state === "unavailable") {
    return <StateBanner tone="warning" title="Insights unavailable" message={insights.message} />;
  }

  return null;
}

function CommandCenter({ context }: { context: ConsoleState }) {
  const { payload, displayDevices, roomScore, statusCounts, anomalyItems, timeline, serviceState, health, openDrawer } = context;
  const navigate = useNavigate();
  const hasTelemetry = displayDevices.some((item) => item.device.reading_count > 0);
  const leaderName = payload?.comparison.leader_device_id
    ? deviceNameById(payload.devices, payload.comparison.leader_device_id)
    : "No leader";
  const scoreRows = displayDevices.map((item, index) => ({
    name: deviceName(item.device, index),
    score: item.score ?? 0,
    fill: DEVICE_COLORS[index % DEVICE_COLORS.length],
    deviceId: item.device.device_id
  }));
  const totalReadings = payload?.devices.reduce((sum, device) => sum + device.reading_count, 0) ?? 0;
  const outside = payload?.outside_context;
  const outsideLatest = outside?.latest_metrics;

  return (
    <div className="stack">
      <PageStatus context={context} />
      <div className="page-head">
        <div>
          <div className="eyebrow">Command Center</div>
          <div className="page-title">{payload?.room_label ?? "LA Office Suite"} Wellness Operations</div>
          <div className="page-sub">
            Inside wellness, replayable telemetry, anomalies, and AI-grounded demo guidance for the configured Wellcubes.
          </div>
        </div>
        <div className="toolbar">
          <button className="btn" onClick={() => navigate("/visualize")} type="button">
            <Layers size={15} />
            Open Visualize
            <ArrowRight size={14} />
          </button>
          <span className={`tag dot ${serviceState.ready ? "good" : "warn"}`}>{serviceState.detail}</span>
        </div>
      </div>

      <div className="kpi-grid">
        <KpiCard icon={<Gauge />} label="Room wellness" value={formatScore(roomScore)} sub={payload?.comparison.summary ?? "Waiting for insights."} tone={scoreTone(roomScore)} onClick={() => navigate("/trends")} />
        <KpiCard icon={<Activity />} label="Fresh devices" value={`${statusCounts.fresh}/${displayDevices.length || 0}`} sub={`${statusCounts.stale + statusCounts.offline} stale or offline`} tone={statusCounts.stale || statusCounts.offline ? "warn" : "good"} onClick={() => navigate("/wellcubes")} />
        <KpiCard icon={<AlertTriangle />} label="Anomaly callouts" value={String(anomalyItems.length)} sub={anomalyItems.length ? "Open operations log" : "No thresholds crossed"} tone={anomalyItems.length ? "warn" : "good"} onClick={() => navigate("/operations")} />
        <KpiCard icon={<Radio />} label="Replay points" value={formatNumber(timeline.length)} sub={`${formatNumber(totalReadings)} indexed readings`} tone={timeline.length ? "info" : "warn"} onClick={() => navigate("/replay")} />
        <KpiCard icon={<ThermometerSun />} label="Outside AQI" value={formatMetric(outsideLatest?.us_aqi, "AQI", 0)} sub={outsideKpiSub(outside)} tone={outsideTone(outside)} onClick={() => navigate("/sources")} />
      </div>

      {!hasTelemetry && payload ? (
        <StateBanner
          title="No indexed telemetry"
          message="Configured Wellcubes are present, but the dashboard has no readings for this window."
          tone="warning"
        />
      ) : null}

      {payload?.room_label.toLowerCase().includes("unconfigured") ? (
        <StateBanner
          title="Room labels unconfigured"
          message="Device IDs are visible, but display labels should be configured for the demo workspace."
          tone="warning"
        />
      ) : null}

      {payload ? <OutsideContextPanel outsideContext={payload.outside_context} /> : null}

      <div className="grid-32">
        <article className="card" aria-label="Wellness score by Wellcube">
          <div className="card-head">
            <div>
              <div className="card-title">Inside wellness by Wellcube</div>
              <div className="card-sub">Click a device row below for component details</div>
            </div>
            <span className="tag">{leaderName}</span>
          </div>
          <div className="card-body">
            <ResponsiveContainer height={300} width="100%">
              <BarChart data={scoreRows} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
                <CartesianGrid stroke="var(--chart-grid)" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" stroke="var(--chart-axis)" tickLine={false} />
                <YAxis domain={[0, 100]} stroke="var(--chart-axis)" tickLine={false} />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(74, 163, 255, 0.08)" }} />
                <Bar dataKey="score" name="Wellness score" radius={[5, 5, 0, 0]}>
                  {scoreRows.map((row) => (
                    <Cell fill={row.fill} key={row.deviceId} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </article>

        <article className="card" aria-label="Freshness mix">
          <div className="card-head">
            <div>
              <div className="card-title">Freshness mix</div>
              <div className="card-sub">Room state across configured devices</div>
            </div>
          </div>
          <div className="card-body">
            <FreshnessDonut counts={statusCounts} />
          </div>
        </article>
      </div>

      <div className="grid-2">
        <article className="card">
          <div className="card-head">
            <div>
              <div className="card-title">Device status</div>
              <div className="card-sub">Current replay timestamp or latest reading</div>
            </div>
            <button className="btn sm" onClick={() => navigate("/wellcubes")} type="button">
              Open devices <ArrowRight size={14} />
            </button>
          </div>
          <DeviceStatusTable devices={displayDevices} openDrawer={openDrawer} />
        </article>

        <article className="card">
          <div className="card-head">
            <div>
              <div className="card-title">Operations callouts</div>
              <div className="card-sub">Highest priority signals from this window</div>
            </div>
            <button className="btn sm" onClick={() => navigate("/operations")} type="button">
              Open log <ArrowRight size={14} />
            </button>
          </div>
          <div className="card-body">
            {anomalyItems.length ? (
              anomalyItems.slice(0, 6).map((anomaly) => (
                <AnomalyRow anomaly={anomaly} key={anomaly.id} onOpen={() => openAnomalyDrawer(openDrawer, anomaly)} />
              ))
            ) : (
              <EmptyState icon={<CheckCircle2 />} title="No active anomalies" message="No anomaly thresholds were crossed in this telemetry window." />
            )}
          </div>
        </article>
      </div>

      <div className="grid-3">
        <SummaryTile icon={<AirVent />} label="Elasticsearch" value={health.state === "ready" && health.payload.elasticsearch_url_configured ? "Configured" : "Pending"} />
        <SummaryTile icon={<Layers />} label="MCP tools" value={health.state === "ready" && health.payload.mcp_url_configured ? "Local ready" : "Pending"} />
        <SummaryTile icon={<Bot />} label="Assistant" value={health.state === "ready" && health.payload.openai_configured ? "Ready" : "Needs key"} />
      </div>
    </div>
  );
}

function WellcubesPage({ context }: { context: ConsoleState }) {
  const { displayDevices, payload, openDrawer } = context;
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");

  const filteredDevices = displayDevices.filter(({ device }, index) => {
    const freshnessOk = filter === "all" || device.freshness === filter;
    const q = query.trim().toLowerCase();
    const queryOk =
      !q ||
      `${deviceName(device, index)} ${device.device_id} ${device.room_label} ${device.freshness}`.toLowerCase().includes(q);
    return freshnessOk && queryOk;
  });

  const freshnessOptions = ["all", "fresh", "lagging", "stale", "offline"];

  return (
    <div className="stack">
      <PageStatus context={context} />
      <div className="page-head">
        <div>
          <div className="eyebrow">Overview</div>
          <div className="page-title">Wellcube Inventory</div>
          <div className="page-sub">
            Device identity, current state, source scores, component scoring, and replay-aware metrics for {payload?.room_label ?? "the room"}.
          </div>
        </div>
        <div className="toolbar">
          <div className="searchbox compact-search">
            <Search />
            <input aria-label="Search Wellcubes" onChange={(event) => setQuery(event.target.value)} placeholder="Search Wellcubes..." value={query} />
          </div>
          <div className="seg" aria-label="Freshness filter">
            {freshnessOptions.map((option) => (
              <button className={filter === option ? "active" : ""} key={option} onClick={() => setFilter(option)} type="button">
                {option}
              </button>
            ))}
          </div>
        </div>
      </div>

      <section className="device-grid" aria-label="Configured Wellcubes">
        {filteredDevices.map((item, index) => (
          <DeviceCard item={item} index={index} key={item.device.device_id} onOpen={() => openDeviceDrawer(openDrawer, item, index)} />
        ))}
      </section>

      {!filteredDevices.length ? <EmptyState icon={<Search />} title="No devices match" message="Adjust the freshness filter or search text." /> : null}
    </div>
  );
}

function TrendsPage({ context }: { context: ConsoleState }) {
  const { payload, displayDevices, openDrawer } = context;
  const [metricKey, setMetricKey] = useState<keyof SeriesPoint>("inside_wellness_score");
  const metric = METRIC_TRENDS.find((item) => item.key === metricKey) ?? METRIC_TRENDS[0];
  const trendRows = useMemo(() => buildMergedTrendRows(payload, metric), [metric, payload]);
  const hasOutsideTrend = trendRows.some((row) => isNumber(row.outside));
  const latestRows = displayDevices.map((item, index) => {
    const latest = item.point ?? ({ ...item.device.latest_metrics, inside_wellness_score: item.device.inside_wellness_score } as SeriesPoint);
    const value = numberValue(latest[metric.key]);
    return {
      name: deviceName(item.device, index),
      deviceId: item.device.device_id,
      value,
      color: DEVICE_COLORS[index % DEVICE_COLORS.length]
    };
  });

  return (
    <div className="stack">
      <PageStatus context={context} />
      <div className="page-head">
        <div>
          <div className="eyebrow">Analytics</div>
          <div className="page-title">Telemetry Trends</div>
          <div className="page-sub">Compare Wellcube signals across the selected telemetry window and replay timeline.</div>
        </div>
        <div className="seg metric-seg" aria-label="Metric selector">
          {METRIC_TRENDS.map((item) => (
            <button className={metric.key === item.key ? "active" : ""} key={item.key} onClick={() => setMetricKey(item.key)} type="button">
              {item.shortLabel}
            </button>
          ))}
        </div>
      </div>

      <div className="grid-32">
        <article className="card" aria-label={`${metric.label} trend`}>
          <div className="card-head">
            <div>
              <div className="card-title">{metric.label} trend</div>
              <div className="card-sub">All configured devices, aligned by replay timestamp</div>
            </div>
            <span className="tag">{metric.unit}</span>
          </div>
          <div className="card-body">
            {trendRows.length ? (
              <ResponsiveContainer height={360} width="100%">
                <LineChart data={trendRows} margin={{ top: 8, right: 20, bottom: 8, left: 0 }}>
                  <CartesianGrid stroke="var(--chart-grid)" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="timestampLabel" stroke="var(--chart-axis)" tickLine={false} />
                  <YAxis stroke="var(--chart-axis)" tickLine={false} tickFormatter={(value) => formatCompactNumber(Number(value))} />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend />
                  {payload?.devices.map((device, index) => (
                    <Line
                      connectNulls
                      dataKey={device.device_id}
                      dot={false}
                      key={device.device_id}
                      name={deviceName(device, index)}
                      stroke={DEVICE_COLORS[index % DEVICE_COLORS.length]}
                      strokeWidth={2.6}
                      type="monotone"
                    />
                  ))}
                  {hasOutsideTrend ? (
                    <Line
                      connectNulls
                      dataKey="outside"
                      dot={false}
                      name={`Outside ${metric.shortLabel}`}
                      stroke="#f4d35e"
                      strokeDasharray="7 5"
                      strokeWidth={2.2}
                      type="monotone"
                    />
                  ) : null}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState icon={<BarChart3 />} title="No trend data" message="This metric has no available values in the current telemetry window." />
            )}
          </div>
        </article>

        <article className="card">
          <div className="card-head">
            <div>
              <div className="card-title">Latest values</div>
              <div className="card-sub">Current replay point by Wellcube</div>
            </div>
          </div>
          <div className="card-body latest-list">
            {latestRows.map((row, index) => (
              <button
                className="metric-row"
                key={row.deviceId}
                onClick={() => {
                  const item = displayDevices[index];
                  if (item) {
                    openDeviceDrawer(openDrawer, item, index);
                  }
                }}
                type="button"
              >
                <span className="swatch" style={{ background: row.color }} />
                <span>{row.name}</span>
                <strong>{formatMetric(row.value, metric.unit, metric.digits)}</strong>
              </button>
            ))}
            {outsideLatestForMetric(payload?.outside_context, metric) !== null ? (
              <div className="metric-row passive">
                <span className="swatch" style={{ background: "#f4d35e" }} />
                <span>Outside</span>
                <strong>{formatMetric(outsideLatestForMetric(payload?.outside_context, metric), metric.unit, metric.digits)}</strong>
              </div>
            ) : null}
          </div>
        </article>
      </div>

      <section className="trend-card-grid" aria-label="Metric trend cards">
        {METRIC_TRENDS.map((item) => (
          <MetricMiniCard context={context} key={item.key} metric={item} onSelect={() => setMetricKey(item.key)} selected={metric.key === item.key} />
        ))}
      </section>
    </div>
  );
}

function VisualizePage({ context }: { context: ConsoleState }) {
  const { telemetryGraph, graphDetail, setGraphDetail, windowHours } = context;
  const [view, setView] = useState<TelemetryGraphView>("3d");
  const graph = telemetryGraph.state === "ready" ? telemetryGraph.payload : null;
  const hasGraphData = Boolean(graph && graph.roots.length && graph.summary.device_count > 0 && graph.summary.node_count > 0);
  const viewLabel = graphViewLabel(view);

  return (
    <div className="stack visualize-page">
      <div className="page-head">
        <div>
          <div className="eyebrow">Visualize</div>
          <div className="page-title">Telemetry Graph</div>
          <div className="page-sub">
            Relationship view for Wellcube scores, components, latest metrics, anomalies, and replay telemetry in the active {windowHours === 168 ? "7d" : `${windowHours}h`} window.
          </div>
        </div>
        <div className="toolbar">
          <div className="seg" aria-label="Graph detail selector">
            {(["summary", "full"] as const).map((detail) => (
              <button className={graphDetail === detail ? "active" : ""} key={detail} onClick={() => setGraphDetail(detail)} type="button">
                {detail === "summary" ? "Summary" : "Full"}
              </button>
            ))}
          </div>
          <div className="seg" aria-label="Graph view selector">
            {(["3d", "2d", "tree"] as const).map((nextView) => (
              <button className={view === nextView ? "active" : ""} key={nextView} onClick={() => setView(nextView)} type="button">
                {graphViewLabel(nextView)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {telemetryGraph.state === "loading" ? (
        <StateBanner icon={<RefreshCw className="spin" size={16} />} title="Loading telemetry graph" message="Fetching the current Telemetry Graph from the Wellcube API." />
      ) : null}

      {telemetryGraph.state === "unavailable" ? (
        <StateBanner tone="warning" title="Telemetry graph unavailable" message={telemetryGraph.message} />
      ) : null}

      {graph && !hasGraphData ? (
        <EmptyState icon={<Layers />} title="No graph data" message="The current Telemetry Graph has no configured Wellcubes for this window." />
      ) : null}

      {graph && hasGraphData ? (
        <>
          <div className="kpi-grid graph-kpis">
            <KpiCard icon={<Layers />} label="Graph nodes" value={formatNumber(graph.summary.node_count)} sub={`${formatNumber(graph.summary.leaf_count)} leaves`} tone="info" />
            <KpiCard icon={<Gauge />} label="Scored devices" value={`${graph.summary.scored_device_count}/${graph.summary.device_count}`} sub={graph.room_label} tone="good" />
            <KpiCard icon={<AlertTriangle />} label="Anomalies" value={formatNumber(graph.summary.anomaly_count)} sub={graph.summary.anomaly_count ? "Included in graph" : "No anomaly nodes"} tone={graph.summary.anomaly_count ? "warn" : "good"} />
            <KpiCard icon={<Radio />} label="Series points" value={formatNumber(graph.summary.series_point_count)} sub={`${graph.detail} detail`} tone={graph.detail === "full" ? "info" : "neutral"} />
          </div>

          <div className="visualize-layout">
            <article className="card visualize-stage" aria-label="Telemetry graph preview">
              <div className="card-head">
                <div>
                  <div className="card-title">{viewLabel}</div>
                  <div className="card-sub">{graph.schema_version} · {graph.detail} detail</div>
                </div>
                <span className="tag">{formatTimestamp(graph.summary.room_anchor_timestamp ?? graph.window.room_anchor_timestamp ?? null)}</span>
              </div>
              {view === "3d" ? (
                <Suspense
                  fallback={
                    <div className="card-body graph3d-loading" aria-label="3D Graph loading">
                      <RefreshCw className="spin" size={16} />
                      <span>Preparing 3D graph</span>
                    </div>
                  }
                >
                  <TelemetryGraph3D graph={graph} />
                </Suspense>
              ) : view === "2d" ? (
                <Suspense
                  fallback={
                    <div className="card-body graph2d-loading" aria-label="2D Graph loading">
                      <RefreshCw className="spin" size={16} />
                      <span>Preparing 2D graph</span>
                    </div>
                  }
                >
                  <TelemetryGraph2D graph={graph} />
                </Suspense>
              ) : (
                <Suspense
                  fallback={
                    <div className="card-body graph2d-loading" aria-label="Data Tree loading">
                      <RefreshCw className="spin" size={16} />
                      <span>Preparing Data Tree</span>
                    </div>
                  }
                >
                  <TelemetryDataTree graph={graph} />
                </Suspense>
              )}
            </article>

            <article className="card">
              <div className="card-head">
                <div>
                  <div className="card-title">Graph contract</div>
                  <div className="card-sub">Stable node fields from the API</div>
                </div>
                <span className="tag">{`${graph.roots.length} ${graph.roots.length === 1 ? "root" : "roots"}`}</span>
              </div>
              <div className="card-body graph-contract">
                <MetricBox label="Room" value={graph.room_label} />
                <MetricBox label="Leader" value={graph.summary.leader_device_id ?? "No leader"} />
                <MetricBox label="Window" value={graph.window.hours ? `${graph.window.hours}h` : `${windowHours}h`} />
                <div className="tree-list" aria-label="Telemetry graph roots">
                  {graph.roots.map((root) => (
                    <GraphTreePreview depth={0} key={root.id} node={root} />
                  ))}
                </div>
              </div>
            </article>
          </div>
        </>
      ) : null}
    </div>
  );
}

function GraphTreePreview({ node, depth }: { node: TelemetryGraphNode; depth: number }) {
  const visibleChildren = node.children.slice(0, depth > 0 ? 3 : 4);
  const hiddenCount = Math.max(0, node.children.length - visibleChildren.length);

  return (
    <div className="tree-node" style={{ "--tree-indent": `${Math.min(depth, 5) * 12}px` } as CSSProperties}>
      <div className="tree-node-row">
        <span className="tree-dot" style={{ background: node.color ?? "var(--line-2)" }} />
        <span className="tree-label">{node.label}</span>
        <span className="tree-value">{node.value ?? (node.leaf ? "Leaf" : `${node.children.length} children`)}</span>
      </div>
      {visibleChildren.map((child) => (
        <GraphTreePreview depth={depth + 1} key={child.id} node={child} />
      ))}
      {hiddenCount ? <div className="tree-more">{formatNumber(hiddenCount)} more</div> : null}
    </div>
  );
}

function OperationsPage({ context }: { context: ConsoleState }) {
  const { anomalyItems, displayDevices, openDrawer } = context;
  const [category, setCategory] = useState("all");
  const [query, setQuery] = useState("");
  const categories = ["all", "Air", "Comfort", "Noise", "Light", "Data Quality", "Other"];
  const categoryCounts = categories.slice(1).map((item) => ({
    name: item,
    value: anomalyItems.filter((anomaly) => anomaly.category === item).length,
    color: categoryColor(item)
  }));
  const filtered = anomalyItems.filter((anomaly) => {
    const categoryOk = category === "all" || anomaly.category === category;
    const q = query.trim().toLowerCase();
    const queryOk =
      !q ||
      `${anomaly.deviceName} ${anomaly.message} ${anomaly.category} ${anomaly.severity}`.toLowerCase().includes(q);
    return categoryOk && queryOk;
  });

  return (
    <div className="stack">
      <PageStatus context={context} />
      <div className="page-head">
        <div>
          <div className="eyebrow">Operations</div>
          <div className="page-title">Anomaly Operations</div>
          <div className="page-sub">Filter sensor callouts, stale/offline states, and data-quality findings for the active telemetry window.</div>
        </div>
        <div className="toolbar">
          <div className="searchbox compact-search">
            <Search />
            <input aria-label="Search anomalies" onChange={(event) => setQuery(event.target.value)} placeholder="Search anomalies..." value={query} />
          </div>
        </div>
      </div>

      <div className="cat-strip">
        {categories.slice(1).map((item) => {
          const count = anomalyItems.filter((anomaly) => anomaly.category === item).length;
          return (
            <button
              className={`kpi category-kpi ${category === item ? "active" : ""}`}
              key={item}
              onClick={() => setCategory(category === item ? "all" : item)}
              style={{ "--category-color": categoryColor(item) } as CSSProperties}
              type="button"
            >
              <div className="kpi-top">
                <span className="kpi-label">{item}</span>
                <span className="kpi-icon">
                  <AlertTriangle />
                </span>
              </div>
              <div className="kpi-value">{count}</div>
            </button>
          );
        })}
      </div>

      <div className="grid-32">
        <article className="card">
          <div className="card-head">
            <div>
              <div className="card-title">Operations log</div>
              <div className="card-sub">{category === "all" ? "All callouts" : category} · {filtered.length}</div>
            </div>
            {category !== "all" ? (
              <button className="btn sm ghost" onClick={() => setCategory("all")} type="button">
                Clear filter <X size={13} />
              </button>
            ) : null}
          </div>
          <div className="card-body ops-log">
            {filtered.length ? (
              filtered.map((anomaly) => (
                <AnomalyRow anomaly={anomaly} key={anomaly.id} onOpen={() => openAnomalyDrawer(openDrawer, anomaly)} />
              ))
            ) : (
              <EmptyState icon={<CheckCircle2 />} title="No callouts in view" message="No anomaly rows match the active filters." />
            )}
          </div>
        </article>

        <article className="card">
          <div className="card-head">
            <div>
              <div className="card-title">Category mix</div>
              <div className="card-sub">Callouts by operating family</div>
            </div>
          </div>
          <div className="card-body">
            {anomalyItems.length ? (
              <>
                <ResponsiveContainer height={220} width="100%">
                  <PieChart>
                    <Pie data={categoryCounts.filter((item) => item.value > 0)} dataKey="value" innerRadius="58%" nameKey="name" outerRadius="86%" stroke="none">
                      {categoryCounts.filter((item) => item.value > 0).map((item) => (
                        <Cell fill={item.color} key={item.name} />
                      ))}
                    </Pie>
                    <Tooltip content={<ChartTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="donut-legend">
                  {categoryCounts.map((item) => (
                    <button className="legend-button" key={item.name} onClick={() => setCategory(item.name)} type="button">
                      <span className="sw" style={{ background: item.color }} />
                      <span className="nm">{item.name}</span>
                      <span className="vl">{item.value}</span>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <EmptyState icon={<CheckCircle2 />} title="No anomalies" message="The current window has no anomaly categories to chart." />
            )}
          </div>
        </article>
      </div>

      <article className="card">
        <div className="card-head">
          <div>
            <div className="card-title">Freshness and source state</div>
            <div className="card-sub">Device lags are treated as operational signals</div>
          </div>
        </div>
        <DeviceStatusTable devices={displayDevices} openDrawer={openDrawer} />
      </article>
    </div>
  );
}

function ReplayPage({ context }: { context: ConsoleState }) {
  const {
    currentReplayTimestamp,
    displayDevices,
    isPlaying,
    replayIndex,
    setIsPlaying,
    setReplayIndex,
    timeline,
    roomScore,
    openDrawer
  } = context;

  return (
    <div className="stack">
      <PageStatus context={context} />
      <div className="page-head">
        <div>
          <div className="eyebrow">Replay</div>
          <div className="page-title">Telemetry Replay</div>
          <div className="page-sub">Scrub the CSV-backed timeline and watch device snapshots, scores, and sensor values update together.</div>
        </div>
        <span className="tag">
          <Clock3 size={13} />
          {formatTimestamp(currentReplayTimestamp)}
        </span>
      </div>

      <article className="replay-console card" aria-label="Replay controls">
        <div className="replay-score">
          <ScoreBadge score={roomScore} label="room score" />
          <div>
            <div className="eyebrow">Current point</div>
            <h2 data-testid="replay-time">{formatTimestamp(currentReplayTimestamp)}</h2>
            <p>{timeline.length ? `${replayIndex + 1} of ${timeline.length} replay points` : "Replay waiting for indexed telemetry."}</p>
          </div>
        </div>
        <div className="replay-controls">
          <button
            aria-label={isPlaying ? "Pause replay" : "Play replay"}
            className="btn primary"
            disabled={timeline.length < 2}
            onClick={() => setIsPlaying(!isPlaying)}
            type="button"
          >
            {isPlaying ? <Pause size={16} /> : <Play size={16} />}
            {isPlaying ? "Pause" : "Play"}
          </button>
          <button
            aria-label="Restart replay"
            className="btn"
            disabled={timeline.length < 2}
            onClick={() => {
              setReplayIndex(0);
              setIsPlaying(false);
            }}
            type="button"
          >
            <RefreshCw size={16} />
            Restart
          </button>
          <input
            aria-label="Replay time"
            disabled={timeline.length < 2}
            max={Math.max(0, timeline.length - 1)}
            min={0}
            onChange={(event) => setReplayIndex(Number(event.target.value))}
            type="range"
            value={replayIndex}
          />
        </div>
      </article>

      <section className="device-grid replay-grid" aria-label="Replay snapshots">
        {displayDevices.map((item, index) => (
          <DeviceCard item={item} index={index} key={item.device.device_id} onOpen={() => openDeviceDrawer(openDrawer, item, index)} />
        ))}
      </section>
    </div>
  );
}

function AIQueryPage({ context }: { context: ConsoleState }) {
  const {
    chatEntries,
    chatInput,
    chatResult,
    copiedEntryId,
    dismissChatEntry,
    health,
    openAiReady,
    setChatInput,
    setCopiedEntryId,
    submitChat
  } = context;
  const busy = chatResult.state === "loading";

  const copyEntry = (entry: ChatEntry) => {
    const text = `Q: ${entry.question}\n\nA: ${entry.answer}`;
    navigator.clipboard.writeText(text);
    setCopiedEntryId(entry.id);
    window.setTimeout(() => setCopiedEntryId(null), 1400);
  };

  return (
    <div className="ai-query-page">
      <PageStatus context={context} />
      <div className="ai-query-hero">
        <h1>
          <Sparkles size={22} />
          AI Query
        </h1>
        <p>Ask the Wellcube assistant about scores, sensor drivers, anomalies, outside context, or demo talk tracks.</p>
      </div>

      <form
        className="ai-query-bar"
        onSubmit={(event) => {
          event.preventDefault();
          submitChat();
        }}
      >
        <Sparkles className="ai-icon" size={16} />
        <input
          aria-label="Assistant question"
          className="ai-query-input"
          disabled={!openAiReady || busy}
          onChange={(event) => setChatInput(event.target.value)}
          placeholder={openAiReady ? "Ask anything about the current telemetry window..." : "OPENAI_API_KEY required"}
          value={chatInput}
        />
        <button className="ai-query-submit" disabled={!openAiReady || busy || !chatInput.trim()} type="submit">
          {busy ? <RefreshCw className="spin" size={13} /> : <Send size={13} />}
          {busy ? "Thinking" : "Query"}
        </button>
      </form>

      <div className="assistant-status" aria-label="Assistant tool status">
        <span>{health.state === "ready" ? health.payload.llm_model : "Model pending"}</span>
        <span>{health.state === "ready" && health.payload.openai_mcp_url_configured ? "Remote MCP" : "Local context"}</span>
        <span>{health.state === "ready" && health.payload.web_mcp_enabled ? "Web enabled" : "Web off"}</span>
        <span>{health.state === "ready" && health.payload.telemetry_memory_enabled ? "Memory on" : "Memory off"}</span>
        <span>{openAiReady ? "Ready" : "Needs key"}</span>
      </div>

      <div className="ai-query-chips" aria-label="Suggested assistant prompts">
        {PROMPT_BUTTONS.map((prompt) => (
          <button disabled={!openAiReady || busy} key={prompt} onClick={() => submitChat(prompt)} type="button">
            {prompt}
          </button>
        ))}
      </div>

      {busy ? (
        <article className="ai-card-shell loading">
          <div className="ai-card-head">
            <span className="ai-pill">
              <RefreshCw className="spin" size={11} /> Analyzing
            </span>
            <span className="ai-card-q">Working through Wellcube insights, tool settings, and current window context.</span>
          </div>
          <div className="ai-skeleton">
            <span />
            <span className="short" />
            <span />
          </div>
        </article>
      ) : null}

      {chatResult.state === "unavailable" ? (
        <StateBanner tone="warning" title="Assistant unavailable" message={chatResult.message} />
      ) : null}

      {chatEntries.map((entry) => (
        <article className="ai-card-shell" key={entry.id}>
          <div className="ai-card-head">
            <div className="ai-card-head-left">
              <span className="ai-pill">
                <Sparkles size={11} /> Answer
              </span>
              <span className="ai-pill alt">{entry.payload.model}</span>
              <span className="ai-pill ghost">{entry.payload.mcp_mode.replace(/_/g, " ")}</span>
              <span className="ai-pill ghost">{entry.payload.web_mcp_enabled ? "web enabled" : "web disabled"}</span>
            </div>
            <div className="ai-card-head-actions">
              <button className="ai-icon-btn" onClick={() => copyEntry(entry)} type="button">
                {copiedEntryId === entry.id ? <CheckCircle2 size={12} /> : <Copy size={12} />}
                {copiedEntryId === entry.id ? "Copied" : "Copy"}
              </button>
              <button aria-label="Dismiss answer" className="ai-icon-btn" onClick={() => dismissChatEntry(entry.id)} type="button">
                <X size={13} />
              </button>
            </div>
          </div>
          <div className="ai-card-q-block">
            <Sparkles size={13} />
            <span>{entry.question}</span>
          </div>
          <div className="ai-summary">
            <div className="ai-summary-text">{entry.answer}</div>
          </div>
          <ToolTransparency payload={entry.payload} />
        </article>
      ))}

      {!chatEntries.length && !busy ? (
        <EmptyState icon={<Bot />} title={openAiReady ? "Ask from the current window" : "Assistant needs configuration"} message={openAiReady ? "Use a suggested prompt or type a telemetry question." : "Set OPENAI_API_KEY to enable live chat through the FastAPI backend."} />
      ) : null}
    </div>
  );
}

function SourcesPage({ context }: { context: ConsoleState }) {
  const { payload, health } = context;
  const outside = payload?.outside_context;
  const grouped = SOURCE_CARDS.reduce<Record<string, typeof SOURCE_CARDS>>((groups, item) => {
    (groups[item.category] ??= []).push(item);
    return groups;
  }, {});

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <div className="eyebrow">Reference</div>
          <div className="page-title">Data Sources & Methodology</div>
          <div className="page-sub">Provenance for the local Wellcube demo, derived scoring, assistant context, and known unavailable signals.</div>
        </div>
      </div>

      <div className="kpi-grid">
        <KpiCard icon={<Clipboard />} label="Source schema" value={payload?.schema_version ?? "--"} sub="Insights API contract" tone="info" />
        <KpiCard icon={<Database />} label="Storage" value={health.state === "ready" && health.payload.elasticsearch_url_configured ? "Elastic" : "Pending"} sub="Local Compose service" tone="info" />
        <KpiCard icon={<Bot />} label="Assistant mode" value={health.state === "ready" && health.payload.openai_mcp_url_configured ? "Remote MCP" : "Local"} sub="Tool transparency preserved" tone="info" />
        <KpiCard icon={<Search />} label="Outside context" value={outside?.state ?? "unavailable"} sub={outsideKpiSub(outside)} tone={outsideTone(outside)} />
      </div>

      <div className="note">
        <b>Methodology.</b> Inside wellness is demo scoring derived from indexed Wellcube readings. It does not overwrite source workspace,
        air-quality, or comfort scores. {outside?.state === "available" ? "Localized outside context is comparison evidence only; it does not change the inside wellness score." : "Outside wellness remains unavailable until a weather, web-search, or provider source is configured."}
      </div>

      {Object.entries(grouped).map(([category, items]) => (
        <section key={category}>
          <h2 className="section-title">{category}</h2>
          {items.map((item) => (
            <article className="src-card" key={item.title}>
              <div className="st">
                {item.title}
                <span className={`tag ${sourceKindTone(item.kind)}`}>{sourceKindLabel(item.kind)}</span>
              </div>
              <div className="sd">{item.description}</div>
              <div className="source-usage">
                <b>Used for:</b> {item.usage}
              </div>
            </article>
          ))}
        </section>
      ))}
    </div>
  );
}

function DeviceCard({ item, index, onOpen }: { item: DisplayDevice; index: number; onOpen: () => void }) {
  const { device, point, score } = item;
  const latest = point ?? ({ ...device.latest_metrics, inside_wellness_score: device.inside_wellness_score } as SeriesPoint);
  const accent = DEVICE_COLORS[index % DEVICE_COLORS.length];

  return (
    <article className="device-card" style={{ "--accent-color": accent } as CSSProperties} aria-label={`${deviceName(device, index)} status`}>
      <div className="device-heading">
        <div className="device-icon">
          <Gauge size={20} />
        </div>
        <div>
          <h2>{deviceName(device, index)}</h2>
          <p>{device.device_id}</p>
        </div>
        <span className={`freshness-badge ${device.freshness}`}>{device.freshness}</span>
      </div>
      <div className="device-score-row">
        <ScoreBadge score={score} label="score" compact />
        <div>
          <strong>{device.score_state.replace(/_/g, " ")}</strong>
          <span>{formatTimestamp(device.latest_timestamp)}</span>
        </div>
      </div>
      <div className="sensor-grid">
        <SensorMetric icon={<AirVent size={16} />} label="Air" value={formatScore(device.source_scores.air_quality_score)} />
        <SensorMetric icon={<ThermometerSun size={16} />} label="Comfort" value={formatScore(device.source_scores.comfort_score)} />
        <SensorMetric icon={<Activity size={16} />} label="Occupancy" value={latest.occupied === true ? "Occupied" : latest.occupied === false ? "Open" : "Unknown"} />
        <SensorMetric icon={<Waves size={16} />} label="Noise" value={formatMetric(latest.noise_dba, "dBA", 0)} />
        <SensorMetric icon={<Lightbulb size={16} />} label="Light" value={formatMetric(latest.light_lux, "lux", 0)} />
        <SensorMetric icon={<Clock3 size={16} />} label="Lag" value={freshnessDetail(device)} />
      </div>
      <ComponentBars components={device.components} />
      <button className="card-cta" onClick={onOpen} type="button">
        Open Wellcube file <ArrowRight size={14} />
      </button>
    </article>
  );
}

function DeviceStatusTable({
  devices,
  openDrawer
}: {
  devices: DisplayDevice[];
  openDrawer: (content: DrawerContent) => void;
}) {
  return (
    <div className="t-wrap">
      <table className="t">
        <thead>
          <tr>
            <th>Wellcube</th>
            <th>Status</th>
            <th className="right">Score</th>
            <th className="right">CO2</th>
            <th className="right">Noise</th>
            <th className="right">Latest</th>
          </tr>
        </thead>
        <tbody>
          {devices.map((item, index) => {
            const latest = item.point ?? ({ ...item.device.latest_metrics, inside_wellness_score: item.device.inside_wellness_score } as SeriesPoint);
            return (
              <tr className="clickable" key={item.device.device_id} onClick={() => openDeviceDrawer(openDrawer, item, index)}>
                <td>
                  <div className="table-device">
                    <span className="swatch" style={{ background: DEVICE_COLORS[index % DEVICE_COLORS.length] }} />
                    <span>
                      <strong>{deviceName(item.device, index)}</strong>
                      <small>{item.device.device_id}</small>
                    </span>
                  </div>
                </td>
                <td>
                  <span className={`freshness-badge ${item.device.freshness}`}>{item.device.freshness}</span>
                </td>
                <td className="right strong">{formatScore(item.score)}</td>
                <td className="right">{formatMetric(latest.co2_ppm, "ppm", 0)}</td>
                <td className="right">{formatMetric(latest.noise_dba, "dBA", 0)}</td>
                <td className="right">{formatShortTimestamp(item.device.latest_timestamp)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function MetricMiniCard({
  context,
  metric,
  selected,
  onSelect
}: {
  context: ConsoleState;
  metric: MetricDefinition;
  selected: boolean;
  onSelect: () => void;
}) {
  const values = context.payload?.devices.flatMap((device) =>
    device.series.map((point) => numberValue(point[metric.key])).filter(isNumber)
  ) ?? [];
  const latest = values.length ? values[values.length - 1] : null;

  return (
    <button className={`trend-mini-card ${selected ? "active" : ""}`} onClick={onSelect} style={{ "--accent-color": metric.color } as CSSProperties} type="button">
      <span>{metric.shortLabel}</span>
      <strong>{formatMetric(latest, metric.unit, metric.digits)}</strong>
      <small>{values.length ? `${values.length} samples` : "No signal"}</small>
    </button>
  );
}

function ToolTransparency({ payload }: { payload: ChatPayload }) {
  const visibleSources = payload.sources.slice(0, 8);
  const visibleTools = payload.tools.slice(0, 6);

  return (
    <div className="tool-transparency" aria-label="Chat tool transparency">
      <div className="source-chips">
        <span>{payload.mcp_mode.replace(/_/g, " ")}</span>
        <span>{payload.web_mcp_enabled ? "web enabled" : "web disabled"}</span>
        {payload.memory ? <span>{`memory ${payload.memory.status ?? "unknown"}`}</span> : null}
      </div>
      {visibleTools.length ? (
        <div className="transparency-list">
          {visibleTools.map((tool, index) => (
            <span key={`${tool.kind}-${tool.label}-${tool.name ?? index}`}>
              {tool.label}
              {tool.name ? `/${tool.name}` : ""}: {tool.status}
            </span>
          ))}
        </div>
      ) : null}
      <div className="transparency-list">
        {visibleSources.map((source, index) =>
          source.url ? (
            <a href={source.url} key={`${source.kind}-${source.label}-${index}`} rel="noreferrer" target="_blank">
              {source.label}
            </a>
          ) : (
            <span key={`${source.kind}-${source.label}-${index}`}>
              {source.label}: {source.status}
            </span>
          )
        )}
      </div>
    </div>
  );
}

function Drawer({ content, mounted, onClose }: { content: DrawerContent; mounted: boolean; onClose: () => void }) {
  return (
    <>
      <button aria-label="Close drawer overlay" className="drawer-overlay" onClick={onClose} type="button" />
      <aside className={`drawer ${mounted ? "open" : ""}`} aria-label={content.title}>
        <div className="drawer-head" style={content.accent ? { borderTopColor: content.accent } : undefined}>
          <div>
            {content.eyebrow ? <div className="eyebrow">{content.eyebrow}</div> : null}
            <h3>{content.title}</h3>
            {content.subtitle ? <p>{content.subtitle}</p> : null}
          </div>
          <button aria-label="Close drawer" className="drawer-close" onClick={onClose} type="button">
            <X size={16} />
          </button>
        </div>
        <div className="drawer-body">{content.body}</div>
      </aside>
    </>
  );
}

function openDeviceDrawer(openDrawer: (content: DrawerContent) => void, item: DisplayDevice, index: number) {
  const { device, point, score } = item;
  const latest = point ?? ({ ...device.latest_metrics, inside_wellness_score: device.inside_wellness_score } as SeriesPoint);

  openDrawer({
    eyebrow: "Wellcube file",
    title: deviceName(device, index),
    subtitle: device.device_id,
    accent: DEVICE_COLORS[index % DEVICE_COLORS.length],
    body: (
      <div className="drawer-stack">
        <div className="metric-hero">
          <div className="metric-hero-l">Inside wellness</div>
          <div className="metric-hero-v">{formatScore(score)}</div>
          <div className="metric-hero-s">{device.score_state.replace(/_/g, " ")} · {formatTimestamp(device.latest_timestamp)}</div>
        </div>
        <div className="metric-grid three">
          <MetricBox label="Air score" value={formatScore(device.source_scores.air_quality_score)} />
          <MetricBox label="Comfort" value={formatScore(device.source_scores.comfort_score)} />
          <MetricBox label="Workspace" value={formatScore(device.source_scores.workspace_score)} />
          <MetricBox label="CO2" value={formatMetric(latest.co2_ppm, "ppm", 0)} />
          <MetricBox label="TVOC" value={formatMetric(latest.tvoc_ugm3, "ug/m3", 0)} />
          <MetricBox label="PM2.5" value={formatMetric(latest.pm25_ugm3, "ug/m3", 1)} />
          <MetricBox label="Temp" value={formatMetric(latest.temp_c, "C", 1)} />
          <MetricBox label="Humidity" value={formatMetric(latest.humidity_pct, "%", 0)} />
          <MetricBox label="Noise" value={formatMetric(latest.noise_dba, "dBA", 0)} />
        </div>
        <div className="drawer-section-head">Score components</div>
        <ComponentBars components={device.components} />
        <div className="drawer-section-head">Explanations</div>
        {device.explanations.map((message) => (
          <div className="kv kv-stack" key={message}>
            <span className="v">{message}</span>
          </div>
        ))}
        <div className="drawer-section-head">Anomalies</div>
        {device.anomalies.length ? (
          device.anomalies.map((anomaly) => (
            <div className="kv kv-stack" key={`${anomaly.code}-${anomaly.message}`}>
              <span className="k">{anomaly.severity}</span>
              <span className="v">{anomaly.message}</span>
            </div>
          ))
        ) : (
          <p className="muted">No device anomalies in this window.</p>
        )}
      </div>
    )
  });
}

function openAnomalyDrawer(openDrawer: (content: DrawerContent) => void, anomaly: AnomalyView) {
  openDrawer({
    eyebrow: anomaly.category,
    title: anomaly.message,
    subtitle: `${anomaly.deviceName} · ${anomaly.severity}`,
    accent: severityColor(anomaly.severity),
    body: (
      <div className="drawer-stack">
        <div className={`note ${anomaly.severity === "critical" ? "danger" : anomaly.severity === "warning" ? "warn" : ""}`}>
          <b>Operational read.</b> This callout comes from the current scored telemetry window and should be interpreted with the device freshness state.
        </div>
        <MetricBox label="Device" value={anomaly.deviceName} />
        <MetricBox label="Metric" value={anomaly.metric ?? "Device state"} />
        <MetricBox label="Value" value={anomaly.value === null ? "n/a" : formatCompactNumber(anomaly.value)} />
        <MetricBox label="Threshold" value={formatThreshold(anomaly.threshold)} />
        <MetricBox label="Latest reading" value={formatTimestamp(anomaly.latestTimestamp)} />
      </div>
    )
  });
}

function KpiCard({
  icon,
  label,
  value,
  sub,
  tone = "info",
  onClick
}: {
  icon: ReactNode;
  label: string;
  value: string;
  sub: string;
  tone?: "good" | "warn" | "danger" | "info" | "neutral";
  onClick?: () => void;
}) {
  const content = (
    <>
      <div className="kpi-top">
        <span className="kpi-label">{label}</span>
        <span className={`kpi-icon ${tone}`}>{icon}</span>
      </div>
      <div className="kpi-value">{value}</div>
      <div className={`kpi-delta ${tone}`}>
        <span>{sub}</span>
      </div>
    </>
  );

  if (onClick) {
    return (
      <button className="kpi clickable" onClick={onClick} type="button">
        {content}
      </button>
    );
  }

  return <div className="kpi">{content}</div>;
}

function SummaryTile({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="summary-tile">
      <span>{icon}</span>
      <div>
        <small>{label}</small>
        <strong>{value}</strong>
      </div>
    </div>
  );
}

function SensorMetric({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="sensor-metric">
      <span>{icon}</span>
      <p>{label}</p>
      <strong>{value}</strong>
    </div>
  );
}

function OutsideContextPanel({ outsideContext }: { outsideContext: OutsideContext }) {
  if (outsideContext.state !== "available") {
    return (
      <StateBanner
        icon={<ThermometerSun size={16} />}
        title="Outside context unavailable"
        message={outsideContext.message || "Localized outside context has not been backfilled yet."}
        tone="warning"
      />
    );
  }

  const latest = outsideContext.latest_metrics ?? {};
  const temperatureDelta = outsideContext.comparisons?.temperature?.inside_minus_outside_c;
  const pm25Delta = outsideContext.comparisons?.pm25?.inside_minus_outside_ugm3;
  const humidityDelta = outsideContext.comparisons?.humidity?.inside_minus_outside_pct;

  return (
    <article className="card" aria-label="Outside context comparison">
      <div className="card-head">
        <div>
          <div className="card-title">Indoor / Outdoor Context</div>
          <div className="card-sub">{outsideContext.summary || "Localized weather and air-quality backfill"}</div>
        </div>
        <span className="tag good">{outsideContext.location?.label ?? "Localized"}</span>
      </div>
      <div className="card-body">
        <div className="metric-grid three">
          <MetricBox label="Outside temp" value={formatMetric(latest.temp_c, "C", 1)} />
          <MetricBox label="Outside humidity" value={formatMetric(latest.humidity_pct, "%", 0)} />
          <MetricBox label="Outside AQI" value={formatMetric(latest.us_aqi, "AQI", 0)} />
          <MetricBox label="Outside PM2.5" value={formatMetric(latest.pm25_ugm3, "ug/m3", 1)} />
          <MetricBox label="Inside temp delta" value={formatSignedMetric(temperatureDelta, "C", 1)} />
          <MetricBox label="Inside PM2.5 delta" value={formatSignedMetric(pm25Delta, "ug/m3", 1)} />
          <MetricBox label="Inside humidity delta" value={formatSignedMetric(humidityDelta, "%", 0)} />
          <MetricBox label="Latest outside" value={formatShortTimestamp(outsideContext.latest_timestamp)} />
          <MetricBox label="Provider" value={formatSourceProvider(outsideContext.provider)} />
        </div>
        {outsideContext.location?.approximate ? (
          <div className="note warn outside-note">Using an approximate location until the exact office coordinate is approved.</div>
        ) : null}
      </div>
    </article>
  );
}

function MetricBox({ label, value }: { label: string; value: string }) {
  return (
    <div className="metric-box">
      <div className="l">{label}</div>
      <div className="v">{value}</div>
    </div>
  );
}

function ScoreBadge({ score, label, compact = false }: { score: number | null; label: string; compact?: boolean }) {
  const value = isNumber(score) ? Math.max(0, Math.min(100, score)) : 0;
  return (
    <div
      aria-label={`${label}: ${isNumber(score) ? score : "unavailable"}`}
      className={`score-badge ${compact ? "compact" : ""}`}
      style={{ "--score": `${value}%` } as CSSProperties}
    >
      <span>{isNumber(score) ? Math.round(score) : "--"}</span>
      <small>{label}</small>
    </div>
  );
}

function ComponentBars({ components }: { components: ComponentScore[] }) {
  if (!components.length) {
    return <p className="component-empty">No score components available.</p>;
  }

  return (
    <div className="component-bars" aria-label="Score components">
      {components.map((component) => (
        <div className="component-row" key={component.name}>
          <span>{component.label}</span>
          <div className="bar-track">
            <i style={{ width: `${Math.max(0, Math.min(100, component.score))}%` }} />
          </div>
          <strong>{Math.round(component.score)}</strong>
        </div>
      ))}
    </div>
  );
}

function AnomalyRow({ anomaly, onOpen }: { anomaly: AnomalyView; onOpen: () => void }) {
  return (
    <button className="flag-row" onClick={onOpen} type="button">
      <span className="fi" style={{ color: severityColor(anomaly.severity) }}>
        <AlertTriangle size={17} />
      </span>
      <span className="flag-main">
        <span className="ft">{anomaly.message}</span>
        <span className="fs">{anomaly.deviceName} · {anomaly.category} · {formatShortTimestamp(anomaly.latestTimestamp)}</span>
      </span>
      <span className={`tag ${anomaly.severity === "critical" ? "danger" : anomaly.severity === "warning" ? "warn" : "info"}`}>
        {anomaly.severity}
      </span>
    </button>
  );
}

function FreshnessDonut({ counts }: { counts: ReturnType<typeof countFreshness> }) {
  const rows = [
    { name: "Fresh", value: counts.fresh, color: "#2bd9a8" },
    { name: "Lagging", value: counts.lagging, color: "#ffb547" },
    { name: "Stale", value: counts.stale, color: "#ff6b86" },
    { name: "Offline", value: counts.offline, color: "#7c86a8" }
  ];
  const nonzero = rows.filter((row) => row.value > 0);

  if (!nonzero.length) {
    return <EmptyState icon={<Radio />} title="No devices" message="No configured devices were returned by the insights API." />;
  }

  return (
    <>
      <ResponsiveContainer height={210} width="100%">
        <PieChart>
          <Pie data={nonzero} dataKey="value" innerRadius="58%" nameKey="name" outerRadius="86%" stroke="none">
            {nonzero.map((row) => (
              <Cell fill={row.color} key={row.name} />
            ))}
          </Pie>
          <Tooltip content={<ChartTooltip />} />
        </PieChart>
      </ResponsiveContainer>
      <div className="donut-legend">
        {rows.map((row) => (
          <div className="li" key={row.name}>
            <span className="sw" style={{ background: row.color }} />
            <span className="nm">{row.name}</span>
            <span className="vl">{row.value}</span>
          </div>
        ))}
      </div>
    </>
  );
}

function ChartTooltip({
  active,
  payload,
  label
}: {
  active?: boolean;
  payload?: Array<{ name?: string; value?: number | string | null }>;
  label?: string;
}) {
  if (!active || !payload?.length) {
    return null;
  }

  return (
    <div className="chart-tooltip">
      {label ? <div className="ct-label">{label}</div> : null}
      {payload.map((item, index) => (
        <div className="ct-val" key={`${item.name ?? index}-${item.value}`}>
          {item.name ? `${item.name}: ` : ""}
          {formatCompactNumber(Number(item.value ?? 0))}
        </div>
      ))}
    </div>
  );
}

function StateBanner({ icon, title, message, tone = "neutral" }: { icon?: ReactNode; title: string; message: string; tone?: "neutral" | "warning" }) {
  return (
    <section className={`state-banner ${tone}`}>
      <span>{icon ?? <AlertTriangle size={18} />}</span>
      <div>
        <h2>{title}</h2>
        <p>{message}</p>
      </div>
    </section>
  );
}

function EmptyState({ icon, title, message }: { icon: ReactNode; title: string; message: string }) {
  return (
    <div className="empty">
      <span>{icon}</span>
      <strong>{title}</strong>
      <p>{message}</p>
    </div>
  );
}

function graphViewLabel(view: TelemetryGraphView) {
  if (view === "2d") {
    return "2D Graph";
  }
  if (view === "tree") {
    return "Data Tree";
  }
  return "3D Graph";
}

async function fetchJson<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, { credentials: "same-origin", ...options });
  if (!response.ok) {
    let message = `${url} returned ${response.status}`;
    try {
      const payload = (await response.json()) as {
        detail?: string | { message?: string; status?: string };
        message?: string;
        status?: string;
      };
      const detail = typeof payload.detail === "object" && payload.detail !== null ? payload.detail : undefined;
      message = payload.message || detail?.message || payload.status || detail?.status || (typeof payload.detail === "string" ? payload.detail : message);
    } catch {
      // Keep the HTTP fallback when an API error is not JSON.
    }
    throw new Error(message);
  }
  return response.json() as Promise<T>;
}

async function postJson<T>(url: string, payload: unknown): Promise<T> {
  return fetchJson<T>(url, {
    body: JSON.stringify(payload),
    headers: {
      "Content-Type": "application/json"
    },
    method: "POST"
  });
}

function resolveServiceState(health: AsyncState<HealthPayload>) {
  if (health.state === "ready") {
    return {
      label: "Online",
      detail: `${health.payload.environment} environment`,
      ready: true
    };
  }

  if (health.state === "loading") {
    return {
      label: "Checking",
      detail: "Health check loading",
      ready: false
    };
  }

  return {
    label: "Shell",
    detail: "API health unavailable",
    ready: false
  };
}

function buildTimeline(payload: InsightsPayload | null): string[] {
  if (!payload) {
    return [];
  }

  const timestamps = new Set<string>();
  payload.devices.forEach((device) => {
    device.series.forEach((point) => {
      if (point.timestamp) {
        timestamps.add(point.timestamp);
      }
    });
  });

  return Array.from(timestamps).sort();
}

function buildDisplayDevices(payload: InsightsPayload | null, timestamp: string | null): DisplayDevice[] {
  if (!payload) {
    return [];
  }

  return payload.devices.map((device) => {
    const point = pointAtTimestamp(device.series, timestamp);
    const score = numberValue(point?.inside_wellness_score) ?? device.inside_wellness_score;
    return { device, point, score };
  });
}

function pointAtTimestamp(series: SeriesPoint[], timestamp: string | null): SeriesPoint | null {
  if (!series.length) {
    return null;
  }

  if (!timestamp) {
    return series[series.length - 1];
  }

  const exact = series.find((point) => point.timestamp === timestamp);
  if (exact) {
    return exact;
  }

  const previous = [...series].reverse().find((point) => point.timestamp && point.timestamp <= timestamp);
  return previous ?? series[0];
}

function buildAnomalyItems(payload: InsightsPayload | null): AnomalyView[] {
  if (!payload) {
    return [];
  }

  return payload.devices.flatMap((device, index) =>
    device.anomalies.map((anomaly, anomalyIndex) => ({
      ...anomaly,
      id: `${device.device_id}-${anomaly.code}-${anomalyIndex}`,
      deviceId: device.device_id,
      deviceName: deviceName(device, index),
      category: anomalyCategory(anomaly),
      latestTimestamp: device.latest_timestamp
    }))
  );
}

function buildMergedTrendRows(payload: InsightsPayload | null, metric: MetricDefinition) {
  const timeline = buildTimeline(payload);
  if (!payload || !timeline.length) {
    return [];
  }

  return timeline.map((timestamp) => {
    const row: Record<string, string | number | null> = {
      timestamp,
      timestampLabel: formatChartTimestamp(timestamp)
    };
    payload.devices.forEach((device) => {
      const point = pointAtTimestamp(device.series, timestamp);
      row[device.device_id] = numberValue(point?.[metric.key]);
    });
    row.outside = outsideValueAtTimestamp(payload.outside_context, timestamp, metric);
    return row;
  });
}

function outsideValueAtTimestamp(
  outsideContext: OutsideContext | undefined,
  timestamp: string,
  metric: MetricDefinition
): number | null {
  if (outsideContext?.state !== "available") {
    return null;
  }
  const outsideField = OUTSIDE_TREND_FIELDS[metric.key];
  if (!outsideField) {
    return null;
  }
  const targetHour = hourKey(timestamp);
  const point = outsideContext.series?.find((item) => item.timestamp && hourKey(item.timestamp) === targetHour);
  return numberValue(point?.[outsideField]);
}

function outsideLatestForMetric(outsideContext: OutsideContext | undefined, metric: MetricDefinition): number | null {
  if (outsideContext?.state !== "available") {
    return null;
  }
  const outsideField = OUTSIDE_TREND_FIELDS[metric.key];
  if (!outsideField) {
    return null;
  }
  return numberValue(outsideContext.latest_metrics?.[outsideField]);
}

function hourKey(value: string | null | undefined): string {
  if (!value) {
    return "";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value.slice(0, 13);
  }
  parsed.setUTCMinutes(0, 0, 0);
  return parsed.toISOString();
}

function countFreshness(devices: DeviceInsight[]) {
  return devices.reduce(
    (counts, device) => {
      if (device.freshness === "fresh") {
        counts.fresh += 1;
      } else if (device.freshness === "lagging") {
        counts.lagging += 1;
      } else if (device.freshness === "offline") {
        counts.offline += 1;
      } else {
        counts.stale += 1;
      }
      return counts;
    },
    { fresh: 0, lagging: 0, stale: 0, offline: 0 }
  );
}

function average(values: Array<number | null>): number | null {
  const present = values.filter(isNumber);
  if (!present.length) {
    return null;
  }
  return Math.round(present.reduce((sum, value) => sum + value, 0) / present.length);
}

function anomalyCategory(anomaly: Anomaly): string {
  const raw = `${anomaly.code} ${anomaly.metric ?? ""}`.toLowerCase();
  if (raw.includes("co2") || raw.includes("tvoc") || raw.includes("pm")) {
    return "Air";
  }
  if (raw.includes("temp") || raw.includes("humidity") || raw.includes("comfort")) {
    return "Comfort";
  }
  if (raw.includes("noise")) {
    return "Noise";
  }
  if (raw.includes("light")) {
    return "Light";
  }
  if (raw.includes("stale") || raw.includes("missing") || raw.includes("offline") || raw.includes("data")) {
    return "Data Quality";
  }
  return "Other";
}

function deviceName(device: DeviceInsight, index: number): string {
  return `Wellcube ${String.fromCharCode(65 + index)}`;
}

function deviceNameById(devices: DeviceInsight[], deviceId: string): string {
  const index = devices.findIndex((device) => device.device_id === deviceId);
  return index >= 0 ? deviceName(devices[index], index) : deviceId;
}

function freshnessDetail(device: DeviceInsight): string {
  if (device.freshness_lag_hours === null || device.freshness === "fresh") {
    return device.freshness;
  }

  if (device.freshness_lag_hours < 1) {
    return `${Math.round(device.freshness_lag_hours * 60)}m lag`;
  }

  return `${Math.round(device.freshness_lag_hours)}h lag`;
}

function numberValue(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function isNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function formatTimestamp(value: string | null | undefined): string {
  if (!value) {
    return "No timestamp";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    hour: "2-digit",
    hour12: false,
    minute: "2-digit",
    month: "short",
    year: "numeric"
  }).format(date);
}

function formatShortTimestamp(value: string | null | undefined): string {
  if (!value) {
    return "--";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    hour: "2-digit",
    hour12: false,
    minute: "2-digit",
    month: "short"
  }).format(date);
}

function formatChartTimestamp(value: string | null | undefined): string {
  if (!value) {
    return "--";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    hour: "2-digit",
    hour12: false,
    month: "short"
  }).format(date);
}

function formatMetric(value: number | null | undefined, unit: string, digits: number): string {
  if (!isNumber(value)) {
    return "--";
  }

  const rounded = value.toLocaleString("en-US", {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits
  });

  return unit ? `${rounded} ${unit}` : rounded;
}

function formatSignedMetric(value: number | null | undefined, unit: string, digits: number): string {
  if (!isNumber(value)) {
    return "--";
  }
  const formatted = formatMetric(Math.abs(value), unit, digits);
  if (value > 0) {
    return `+${formatted}`;
  }
  if (value < 0) {
    return `-${formatted}`;
  }
  return formatted;
}

function outsideTone(outsideContext: OutsideContext | undefined): "good" | "warn" | "danger" | "info" | "neutral" {
  if (outsideContext?.state !== "available") {
    return "warn";
  }
  const aqi = numberValue(outsideContext.latest_metrics?.us_aqi);
  if (aqi === null) {
    return "info";
  }
  if (aqi <= 50) {
    return "good";
  }
  if (aqi <= 100) {
    return "info";
  }
  if (aqi <= 150) {
    return "warn";
  }
  return "danger";
}

function outsideKpiSub(outsideContext: OutsideContext | undefined): string {
  if (!outsideContext) {
    return "Waiting for outside context";
  }
  if (outsideContext.state !== "available") {
    return outsideContext.message || "No localized outside data";
  }
  return outsideContext.location?.label ?? "Localized outside context";
}

function formatSourceProvider(provider: string | undefined): string {
  if (!provider) {
    return "--";
  }
  return provider.replace(/_/g, " ");
}

function formatScore(value: number | null | undefined): string {
  return isNumber(value) ? String(Math.round(value)) : "--";
}

function formatNumber(value: number): string {
  return value.toLocaleString("en-US");
}

function formatCompactNumber(value: number): string {
  if (!Number.isFinite(value)) {
    return "--";
  }

  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: Math.abs(value) >= 100 ? 0 : 1,
    notation: Math.abs(value) >= 1000 ? "compact" : "standard"
  }).format(value);
}

function formatThreshold(value: Anomaly["threshold"]): string {
  if (value === null) {
    return "n/a";
  }

  if (typeof value === "number") {
    return formatCompactNumber(value);
  }

  return `${formatCompactNumber(value.low)} to ${formatCompactNumber(value.high)}`;
}

function scoreTone(score: number | null): "good" | "warn" | "danger" | "info" {
  if (!isNumber(score)) {
    return "warn";
  }
  if (score >= 80) {
    return "good";
  }
  if (score >= 60) {
    return "warn";
  }
  return "danger";
}

function severityColor(severity: string): string {
  if (severity === "critical") {
    return "#ff6b86";
  }
  if (severity === "warning") {
    return "#ffb547";
  }
  return "#4aa3ff";
}

function categoryColor(category: string): string {
  if (category === "Air") {
    return "#4aa3ff";
  }
  if (category === "Comfort") {
    return "#ffb547";
  }
  if (category === "Noise") {
    return "#ff8c3a";
  }
  if (category === "Light") {
    return "#f4d35e";
  }
  if (category === "Data Quality") {
    return "#ff6b86";
  }
  return "#7c86a8";
}

function sourceKindTone(kind: string): string {
  if (kind === "real" || kind === "configured") {
    return "good";
  }
  if (kind === "modeled") {
    return "warn";
  }
  return "ghost";
}

function sourceKindLabel(kind: string): string {
  if (kind === "real") {
    return "Real data";
  }
  if (kind === "modeled") {
    return "Derived";
  }
  if (kind === "configured") {
    return "Configured";
  }
  return "Pending";
}
